import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'
import * as z from 'zod'

import { PageEventType, TrackEventType } from './eventTypes'
import type { TrackOptions } from './useRudderstack'
import { useRudderstack } from './useRudderstack'

import type { ErrorStates } from 'hooks/useSingleTrackAudioPlayer/types'
import { assertNever } from 'utils/guards'
import { useSettings } from 'utils/settings'

/**
 * Retrieve the page name from the `body.data-page-name` attribute.
 */
export const getPageNameFromBody = (): PageEventType | null => {
  const attr = document.body.getAttribute('data-page-name')

  try {
    return z.nativeEnum(PageEventType).nullable().parse(attr)
  } catch {
    return null
  }
}

type HookProps = {
  /**
   * This is the analytics name of the page (a {@link PageEventType}.
   * Not the HTML title.
   */
  pageName: PageEventType | null
}

/**
 * The properties required by various track events depend on the event.
 * So this is a big discriminated union of possible values that should line up
 * with {@link ZTrackEvent}. Note, not every {@link TrackEventType} is usable
 * with this hook, so some are missing.
 *
 * N.B. `custom` could be broken up into separate fields, but it means those
 * types need to be included with every entry.
 */
type CallbackProps =
  | {
      // Nothing
      event:
        | TrackEventType.START_LISTENING_BUTTON_CLICKED
        | TrackEventType.PLAY_BUTTON_CLICKED
        | TrackEventType.MUTE_BUTTON_CLICKED
        | TrackEventType.UNMUTE_BUTTON_CLICKED
        | TrackEventType.AUDIO_STARTED_PLAYBACK
        | TrackEventType.TRANSCRIPT_OPENED
        | TrackEventType.TRANSCRIPT_CLOSED
        | TrackEventType.INFO_MODAL_OPENED
        | TrackEventType.INFO_MODAL_CLOSED
      target?: never
      targetLocation?: never
      contextualEntities?: never
      eventSource?: never
      custom?: never
    }
  | {
      event:
        | TrackEventType.PLAY
        | TrackEventType.PLAY_NEW_TRACK
        | TrackEventType.ACTIVE_FINISHED
        | TrackEventType.ACTIVE_FINISHED_AT_INIT
      target?: never
      targetLocation?: never
      contextualEntities?: never
      eventSource?: never
      custom: { content_id: string }
    }
  | {
      event: TrackEventType.MUTE_TOGGLE
      target?: never
      targetLocation?: never
      contextualEntities?: never
      eventSource?: never
      custom: { isNowMuted: boolean }
    }
  | {
      event: TrackEventType.PLAY_ERROR
      target?: never
      targetLocation?: never
      contextualEntities?: never
      eventSource?: never
      custom: { content_id: string; circumstance: ErrorStates }
    }

/** A callback function */
type RType = (props: CallbackProps) => void

/**
 * Hook to track visitor events. Visitor events are a type of `track` event.
 *
 * The properties that need to be passed depend on the event type.
 *
 * @returns This hook returns a callback that can be used in an `onClick` function.
 */
export const useVisitorEventTracking = ({ pageName }: HookProps): RType => {
  const { track } = useRudderstack()
  const {
    settings: { defaultLanguage },
  } = useSettings()
  const {
    i18n: { resolvedLanguage = defaultLanguage },
  } = useTranslation()

  return useCallback(
    ({
      event,
      target,
      targetLocation,
      eventSource,
      contextualEntities,
      custom,
    }: CallbackProps) => {
      const common = {
        page_title: pageName,
        page_url: window.location.href,
        // Not 100% accurate, but it's the best we can do for the moment
        page_referrer: document.referrer || null,
        locale: resolvedLanguage,
      }

      let payload: TrackOptions

      switch (event) {
        // Nothing
        case TrackEventType.START_LISTENING_BUTTON_CLICKED:
        case TrackEventType.PLAY_BUTTON_CLICKED:
        case TrackEventType.MUTE_BUTTON_CLICKED:
        case TrackEventType.UNMUTE_BUTTON_CLICKED:
        case TrackEventType.AUDIO_STARTED_PLAYBACK:
        case TrackEventType.TRANSCRIPT_OPENED:
        case TrackEventType.TRANSCRIPT_CLOSED:
        case TrackEventType.INFO_MODAL_OPENED:
        case TrackEventType.INFO_MODAL_CLOSED:
          payload = {
            event,
            properties: {
              ...common,
              custom: {},
            },
          } satisfies TrackOptions
          break

        case TrackEventType.PLAY:
        case TrackEventType.PLAY_NEW_TRACK:
        case TrackEventType.ACTIVE_FINISHED:
        case TrackEventType.ACTIVE_FINISHED_AT_INIT:
          payload = {
            event,
            properties: {
              ...common,
              custom,
            },
          } satisfies TrackOptions
          break

        case TrackEventType.MUTE_TOGGLE:
          payload = {
            event,
            properties: {
              ...common,
              custom,
            },
          } satisfies TrackOptions
          break
        case TrackEventType.PLAY_ERROR:
          payload = {
            event,
            properties: {
              ...common,
              custom,
            },
          } satisfies TrackOptions
          break

        default:
          assertNever(event)
      }

      track(payload)
    },
    [pageName, resolvedLanguage, track],
  )
}
